import type { ReactNode } from "react";

import { cn } from "@/lib/cn";

export function Panel({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <section
      className={cn(
        // Panel wraps every wizard step, so its padding sets the usable width
        // on every one of them: px-8 leaves 267px of content on a 375px phone.
        "mb-4.5 rounded-card bg-surface px-5 py-6 shadow-card sm:px-8 sm:py-7",
        className,
      )}
    >
      {children}
    </section>
  );
}

interface PanelHeadProps {
  /** Small monospace kicker, e.g. "Schritt 1 von 6". */
  eyebrow?: string;
  title: string;
  children?: ReactNode;
}

export function PanelHead({ eyebrow, title, children }: PanelHeadProps) {
  return (
    <header className="mb-5.5">
      {eyebrow && <span className="eyebrow">{eyebrow}</span>}
      <h2 className="mb-1.5 text-[21px] font-bold sm:text-[25px]">{title}</h2>
      {children && <p className="m-0 max-w-[68ch] text-[14.5px] text-muted">{children}</p>}
    </header>
  );
}

/** Footer row: back on the left, hint in the middle, primary action right. */
export function PanelActions({ children }: { children: ReactNode }) {
  return (
    <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-line-soft pt-5">
      {children}
    </div>
  );
}

export function ActionHint({ children }: { children: ReactNode }) {
  return <span className="text-[13px] text-muted">{children}</span>;
}

/**
 * Pushes the following siblings to the right of a PanelActions row.
 *
 * Hidden below sm: once that row wraps on a phone, a flex-1 spacer claims an
 * entire blank line between the back and forward buttons.
 */
export function Spacer() {
  return <span className="hidden flex-1 sm:block" />;
}
